import React, { useState, useEffect } from 'react';
import { 
  User, Lock, Gift, Plus, Trash2, LogOut, 
  CreditCard, History, Users, Ticket, CheckCircle, XCircle, Award, 
  Zap, ChevronRight, Crown, Sparkles, Loader, Eye, RotateCcw, Search,
  Image as ImageIcon, Target, AlertTriangle, ArrowUpDown, Calendar,
  MessageCircle, Copy, FileText, Hash, Info, Trophy, ListChecks, Gamepad2, Dices
} from 'lucide-react';
import { initializeApp } from "firebase/app";
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged 
} from "firebase/auth";
import { 
  getFirestore, 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  onSnapshot, 
  query, 
  where, 
  getDocs,
  setDoc,
  serverTimestamp,
  writeBatch,
  increment
} from "firebase/firestore";

// --- KONFIGURASI FIREBASE ---
const firebaseConfig = {
  apiKey: "AIzaSyCE2AsiUkhkmP-JrlFNJWHMUY6xCGdzKTI",
  authDomain: "royalepoints.firebaseapp.com",
  projectId: "royalepoints",
  storageBucket: "royalepoints.firebasestorage.app",
  messagingSenderId: "623412629036",
  appId: "1:623412629036:web:09881589aa21acffbacb89"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// --- KOMPONEN UI ---

const Button = ({ children, onClick, variant = 'primary', className = '', type = 'button', disabled = false }) => {
  const baseStyle = "relative overflow-hidden px-4 py-2 md:px-6 md:py-3 rounded-xl font-bold uppercase tracking-wider transition-all duration-300 transform hover:-translate-y-1 active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-xs md:text-sm";
  
  const variants = {
    primary: "bg-gradient-to-r from-yellow-500 to-amber-600 text-black shadow-[0_0_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_30px_rgba(245,158,11,0.6)] border border-yellow-400/20",
    secondary: "bg-black/40 backdrop-blur-md text-gray-300 border border-white/10 hover:bg-white/10 hover:text-white hover:border-white/30",
    danger: "bg-gradient-to-r from-red-600 to-rose-800 text-white shadow-[0_0_20px_rgba(225,29,72,0.4)] hover:shadow-[0_0_30px_rgba(225,29,72,0.6)]",
    success: "bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-[0_0_20px_rgba(16,185,129,0.4)] hover:shadow-[0_0_30px_rgba(16,185,129,0.6)]",
    whatsapp: "bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-[0_0_20px_rgba(34,197,94,0.3)] hover:shadow-[0_0_30px_rgba(34,197,94,0.6)] border border-green-400/20",
    outline: "bg-transparent border-2 border-yellow-500/50 text-yellow-500 hover:bg-yellow-500 hover:text-black"
  };

  return (
    <button type={type} onClick={onClick} disabled={disabled} className={`${baseStyle} ${variants[variant]} ${className}`}>
      <span className="relative z-10 flex items-center gap-2">{children}</span>
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
    </button>
  );
};

const Input = ({ label, type, placeholder, value, onChange, required = false, icon: Icon }) => (
  <div className="mb-4 group w-full">
    {label && <label className="block text-gray-400 text-[10px] md:text-xs font-bold mb-2 uppercase tracking-widest group-focus-within:text-yellow-500 transition-colors">{label}</label>}
    <div className="relative">
      {Icon && <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-yellow-500 transition-colors" />}
      <input
        type={type}
        className={`w-full bg-black/50 border border-gray-700 rounded-xl py-3 ${Icon ? 'pl-10' : 'pl-4'} pr-4 text-gray-200 text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-all duration-300`}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
      />
    </div>
  </div>
);

const Select = ({ label, value, onChange, options, icon: Icon }) => (
  <div className="mb-4 group w-full">
    {label && <label className="block text-gray-400 text-[10px] md:text-xs font-bold mb-2 uppercase tracking-widest group-focus-within:text-yellow-500 transition-colors">{label}</label>}
    <div className="relative">
      {Icon && <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-yellow-500 transition-colors" />}
      <select
        className={`w-full bg-black/50 border border-gray-700 rounded-xl py-3 ${Icon ? 'pl-10' : 'pl-4'} pr-4 text-gray-200 text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-all duration-300 appearance-none cursor-pointer`}
        value={value}
        onChange={onChange}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} className="bg-neutral-900 text-gray-200">{opt.label}</option>
        ))}
      </select>
      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
        <ArrowUpDown size={12} className="text-gray-500" />
      </div>
    </div>
  </div>
);

const TextArea = ({ label, placeholder, value, onChange, rows = 3 }) => (
    <div className="mb-4 group w-full">
      {label && <label className="block text-gray-400 text-[10px] md:text-xs font-bold mb-2 uppercase tracking-widest group-focus-within:text-yellow-500 transition-colors">{label}</label>}
      <textarea
        className="w-full bg-black/50 border border-gray-700 rounded-xl py-3 px-4 text-gray-200 text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-all duration-300 custom-scrollbar resize-none"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        rows={rows}
      />
    </div>
);

const Card = ({ title, children, className = '', glow = false, action }) => (
  <div className={`relative bg-neutral-900/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6 overflow-hidden ${glow ? 'shadow-[0_0_40px_rgba(245,158,11,0.1)]' : 'shadow-xl'} ${className}`}>
    <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-yellow-600/10 to-red-600/10 rounded-full blur-3xl pointer-events-none"></div>
    
    {title && (
      <div className="flex items-center justify-between mb-6 border-b border-white/10 pb-4">
        <h3 className="text-lg md:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 to-amber-500 uppercase tracking-widest">{title}</h3>
        {action ? action : <div className="h-1 w-10 bg-gradient-to-r from-red-600 to-yellow-600 rounded-full"></div>}
      </div>
    )}
    <div className="relative z-10">
      {children}
    </div>
  </div>
);

const Badge = ({ children, color = 'blue', className = '' }) => {
  const styles = {
    gold: "bg-yellow-500/10 text-yellow-400 border border-yellow-500/30",
    silver: "bg-slate-500/10 text-slate-300 border border-slate-500/30",
    bronze: "bg-orange-700/10 text-orange-400 border border-orange-700/30",
    platinum: "bg-cyan-500/10 text-cyan-300 border border-cyan-500/30 shadow-[0_0_10px_rgba(6,182,212,0.3)]",
    red: "bg-red-500/10 text-red-400 border border-red-500/30",
    green: "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30",
    purple: "bg-purple-500/10 text-purple-400 border border-purple-500/30",
    blue: "bg-blue-500/10 text-blue-400 border border-blue-500/30",
  };
  return <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${styles[color] || styles.gold} ${className}`}>{children}</span>;
};

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="relative w-full max-w-2xl bg-neutral-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-scale-up">
         <div className="flex justify-between items-center p-6 border-b border-white/10 bg-black/40">
            <h3 className="text-xl font-bold text-white tracking-widest uppercase">{title}</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-white transition"><XCircle size={24} /></button>
         </div>
         <div className="p-6 max-h-[80vh] overflow-y-auto custom-scrollbar">
            {children}
         </div>
      </div>
    </div>
  );
};

// --- LOGIKA UTAMA ---

export default function App() {
  const [users, setUsers] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [claims, setClaims] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  // States: Auth & Forms
  // PERUBAHAN: Default category sekarang 'togel'
  const [formData, setFormData] = useState({ username: '', password: '', category: 'togel' }); 
  const [isRegistering, setIsRegistering] = useState(false);
  
  // States: Admin - Vouchers
  const [voucherCodeInput, setVoucherCodeInput] = useState('');
  const [newVoucherPoints, setNewVoucherPoints] = useState(100);
  const [voucherSearchTerm, setVoucherSearchTerm] = useState('');
  const [voucherFilter, setVoucherFilter] = useState('all');

  // States: Admin - Rewards (Updated with quantity and terms)
  const [newReward, setNewReward] = useState({ name: '', image: '', points: 0, quantity: 10, terms: '', category: 'slot' }); // Added category default

  // States: Admin - User Management
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'points', direction: 'desc' });
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);

  // States: User - Claim Reward
  const [claimSuccessData, setClaimSuccessData] = useState(null); // { code, rewardName }
  const [isClaimModalOpen, setIsClaimModalOpen] = useState(false);
  const [selectedRewardInfo, setSelectedRewardInfo] = useState(null); // For viewing T&C

  // --- LISTENERS ---

  // 1. GLOBAL AUTH LISTENER
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Listener Profil User (Dengan Error Handling)
        const unsubUser = onSnapshot(doc(db, 'users', user.uid), 
          (docSnap) => {
            if (docSnap.exists()) {
              setCurrentUser({ id: user.uid, ...docSnap.data() });
            }
          },
          (error) => {
            console.warn("User profile sync paused (likely permission/auth issue):", error.message);
            // Jangan set currentUser null di sini agar tidak flicker, biarkan auth state menangani
          }
        );
        // Kita tidak bisa dengan mudah unsubscribe ini di dalam onAuthStateChanged tanpa ref
        // Tapi karena ini SPA sederhana, ini acceptable.
      } else {
        setCurrentUser(null);
        setUsers([]);
        setVouchers([]);
        setRewards([]);
        setClaims([]);
      }
      setLoading(false);
    });
    return () => unsubscribeAuth();
  }, []);

  // 2. DATA LISTENER (Vouchers & Rewards)
  useEffect(() => {
    if (!currentUser) return;

    // Vouchers Listener with Error Handling
    const unsubVouchers = onSnapshot(query(collection(db, 'vouchers')), 
      (snap) => {
        setVouchers(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a,b) => b.createdAt?.seconds - a.createdAt?.seconds));
      }, 
      (error) => console.log("Voucher sync warning:", error.code)
    );

    // Rewards Listener with Error Handling
    const unsubRewards = onSnapshot(query(collection(db, 'rewards')), 
      (snap) => {
        setRewards(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a,b) => a.points - b.points));
      },
      (error) => console.log("Reward sync warning:", error.code)
    );

    return () => { unsubVouchers(); unsubRewards(); };
  }, [currentUser?.id]);

  // 3. ADMIN LISTENER (Users)
  useEffect(() => {
    if (!currentUser || currentUser.role !== 'admin') return;

    // Users Listener with Error Handling
    const unsubUsers = onSnapshot(query(collection(db, 'users')), 
      (snap) => {
        setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      },
      (error) => console.log("Admin user sync warning:", error.code)
    );

    // Claims Listener
    const unsubClaims = onSnapshot(query(collection(db, 'claims')), 
      (snap) => {
        setClaims(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a,b) => b.createdAt?.seconds - a.createdAt?.seconds));
      },
      (error) => console.log("Claims sync warning:", error.code)
    );

    return () => { unsubUsers(); unsubClaims(); };
  }, [currentUser?.role, currentUser?.id]);

  // --- HELPERS ---

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const generateCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let result = '';
    for (let i = 0; i < 8; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
    return result;
  };

  const formatNumber = (num) => new Intl.NumberFormat('id-ID').format(num || 0);

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const getLevelInfo = (points = 0) => {
    if (points <= 500) return { name: 'Bronze', color: 'bronze', textColor: 'text-orange-400', gradient: 'from-orange-900 to-neutral-900', border: 'border-orange-700/50', icon: Award };
    if (points <= 1000) return { name: 'Silver', color: 'silver', textColor: 'text-slate-300', gradient: 'from-slate-800 to-neutral-900', border: 'border-slate-500/50', icon: Award };
    if (points <= 3000) return { name: 'Gold', color: 'gold', textColor: 'text-yellow-400', gradient: 'from-yellow-900 to-neutral-900', border: 'border-yellow-500/50', icon: Crown };
    return { name: 'Platinum', color: 'platinum', textColor: 'text-cyan-400', gradient: 'from-cyan-900 to-neutral-900', border: 'border-cyan-500/50', icon: Sparkles };
  };

  // --- ACTIONS: AUTH ---

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      await signInWithEmailAndPassword(auth, `${formData.username.toLowerCase()}@royalepoints.com`, formData.password);
      showNotification(`Welcome back, ${formData.username}!`);
    } catch (err) {
      showNotification('Login Failed. Check credentials.', 'error');
    }
    setAuthLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    if (formData.password.length < 6) { showNotification("Password min 6 chars.", "error"); setAuthLoading(false); return; }
    try {
      const email = `${formData.username.toLowerCase()}@royalepoints.com`;
      const cred = await createUserWithEmailAndPassword(auth, email, formData.password);
      await setDoc(doc(db, 'users', cred.user.uid), {
        username: formData.username,
        email,
        role: formData.username.toLowerCase() === 'admin' ? 'admin' : 'user',
        category: formData.category, // Save category
        points: 0,
        history: [],
        createdAt: serverTimestamp()
      });
      showNotification('Account Created!');
    } catch (err) {
      showNotification(err.code === 'auth/email-already-in-use' ? 'Username taken.' : 'Registration Failed.', 'error');
    }
    setAuthLoading(false);
  };

  const handleLogout = async () => { await signOut(auth); };

  // --- ACTIONS: VOUCHERS ---

  const handleCreateVoucher = async () => {
    await addDoc(collection(db, 'vouchers'), {
      code: generateCode(),
      points: parseInt(newVoucherPoints),
      isUsed: false,
      createdAt: serverTimestamp(),
      createdBy: currentUser.username
    });
    showNotification('Voucher Generated');
  };

  const handleDeleteVoucher = async (id) => {
    await deleteDoc(doc(db, 'vouchers', id));
    showNotification('Voucher Deleted');
  };

  const handleRedeemVoucher = async (e) => {
    e.preventDefault();
    if (!voucherCodeInput) return;
    const code = voucherCodeInput.toUpperCase().trim();
    setAuthLoading(true);
    try {
      const q = query(collection(db, 'vouchers'), where('code', '==', code));
      const snap = await getDocs(q);
      if (snap.empty) { showNotification('Invalid Code', 'error'); setAuthLoading(false); return; }
      
      const vDoc = snap.docs[0];
      const vData = vDoc.data();
      if (vData.isUsed) { showNotification('Already Used', 'error'); setAuthLoading(false); return; }

      const batch = writeBatch(db);
      batch.update(doc(db, 'vouchers', vDoc.id), { isUsed: true, usedBy: currentUser.username, usedAt: new Date().toLocaleString() });
      
      const newHistoryItem = { id: Date.now(), activity: `REDEEM: ${code}`, amount: `+${vData.points}`, date: new Date().toLocaleString() };
      batch.update(doc(db, 'users', currentUser.id), { points: (currentUser.points || 0) + parseInt(vData.points), history: [newHistoryItem, ...(currentUser.history || [])] });
      
      await batch.commit();
      showNotification(`+${vData.points} POINTS ADDED!`);
      setVoucherCodeInput('');
    } catch (err) { showNotification('Redemption Error', 'error'); }
    setAuthLoading(false);
  };

  // --- ACTIONS: REWARDS CLAIM ---

  const handleClaimReward = async (reward) => {
    if (currentUser.points < reward.points) {
      showNotification('Insufficient Points', 'error');
      return;
    }
    
    // Check quantity
    if (reward.quantity !== undefined && reward.quantity <= 0) {
        showNotification('Item Out of Stock!', 'error');
        return;
    }

    const claimCode = `REW-${generateCode().substring(0, 4)}-${generateCode().substring(0, 4)}`;

    try {
      const batch = writeBatch(db);
      
      // 1. Deduct Points & Add History (NOW INCLUDES CODE FOR HISTORY)
      const userRef = doc(db, 'users', currentUser.id);
      const newHistoryItem = { 
        id: Date.now(), 
        activity: `CLAIM: ${reward.name}`, 
        amount: `-${reward.points}`, 
        date: new Date().toLocaleString(),
        code: claimCode, // Saving code to history
        rewardName: reward.name, // Saving name
        rewardImage: reward.image // Saving image
      };
      
      batch.update(userRef, { 
        points: currentUser.points - reward.points, 
        history: [newHistoryItem, ...(currentUser.history || [])] 
      });

      // 2. Decrement Stock
      const rewardRef = doc(db, 'rewards', reward.id);
      batch.update(rewardRef, {
        quantity: increment(-1)
      });

      // 3. Create Claim Record
      const claimRef = doc(collection(db, 'claims'));
      batch.set(claimRef, {
        userId: currentUser.id,
        username: currentUser.username,
        rewardId: reward.id,
        rewardName: reward.name,
        claimCode: claimCode,
        status: 'pending',
        pointsSpent: reward.points,
        createdAt: serverTimestamp()
      });

      await batch.commit();

      setClaimSuccessData({ 
        code: claimCode, 
        rewardName: reward.name,
        rewardImage: reward.image
      });
      setIsClaimModalOpen(true);
      showNotification('Reward Claimed Successfully!');

    } catch (error) {
      console.error(error);
      showNotification('Claim Failed. Try again.', 'error');
    }
  };

  const openLiveChat = () => {
    if (!claimSuccessData) return;
    const message = `Hello Admin, I have claimed a reward.\n\nUsername: ${currentUser.username}\nReward: ${claimSuccessData.rewardName}\nClaim Code: ${claimSuccessData.code}\n\nPlease confirm my reward.`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  // Filtered Vouchers Logic
  const filteredVouchers = vouchers.filter(v => {
    const matchesSearch = v.code.toLowerCase().includes(voucherSearchTerm.toLowerCase());
    const matchesFilter = voucherFilter === 'all' 
      ? true 
      : voucherFilter === 'active' ? !v.isUsed : v.isUsed;
    return matchesSearch && matchesFilter;
  });

  // --- ACTIONS: ADMIN MEMBER MANAGEMENT ---

  const handleResetAllPoints = async () => {
    try {
      const batch = writeBatch(db);
      users.forEach(user => {
        const userRef = doc(db, 'users', user.id);
        const resetLog = { id: Date.now() + Math.random(), activity: 'SYSTEM RESET', amount: 'RESET', date: new Date().toLocaleString() };
        batch.update(userRef, { points: 0, history: [resetLog] }); 
      });
      await batch.commit();
      showNotification('ALL POINTS RESET TO 0');
      setIsResetConfirmOpen(false);
    } catch (error) {
      showNotification('Reset Failed', 'error');
    }
  };

  // Sorting Logic
  const sortedUsers = [...users].filter(u => u.username.toLowerCase().includes(searchTerm.toLowerCase())).sort((a, b) => {
    if (sortConfig.key === 'points') {
      return sortConfig.direction === 'asc' ? a.points - b.points : b.points - a.points;
    }
    return sortConfig.direction === 'asc' ? a.username.localeCompare(b.username) : b.username.localeCompare(a.username);
  });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  // --- ACTIONS: REWARDS ---

  const handleCreateReward = async () => {
    if (!newReward.name || !newReward.points) return;
    await addDoc(collection(db, 'rewards'), {
      ...newReward,
      points: parseInt(newReward.points),
      quantity: parseInt(newReward.quantity),
      createdAt: serverTimestamp()
    });
    showNotification('Reward Added');
    setNewReward({ name: '', image: '', points: 0, quantity: 10, terms: '', category: 'slot' }); // Reset with default
  };

  const handleDeleteReward = async (id) => {
    await deleteDoc(doc(db, 'rewards', id));
    showNotification('Reward Deleted');
  };

  // --- BACKGROUND ---
  const Background = () => (
    <div className="fixed inset-0 bg-neutral-950 -z-20 overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_0%,rgba(180,50,50,0.15),transparent_70%)]"></div>
      <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_100%_100%,rgba(234,179,8,0.1),transparent_50%)]"></div>
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px] opacity-20"></div>
    </div>
  );

  // --- RENDER ---

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 font-sans text-gray-200">
        <Background />
        <div className="max-w-md w-full relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-red-600 to-yellow-600 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000"></div>
            <Card className="relative z-10 !bg-black/90" glow>
                <div className="text-center mb-8">
                  <div className="w-24 h-24 mx-auto mb-4 relative flex items-center justify-center">
                      <img src="https://img-zone.b-cdn.net/logo.png" alt="RoyalePoints Logo" className="w-full h-full object-contain drop-shadow-[0_0_10px_rgba(234,179,8,0.8)]" />
                  </div>
                  <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-600 uppercase tracking-[0.2em]">Royale<span className="text-red-600">Points</span></h1>
                </div>
                {notification && <div className={`mb-6 p-3 rounded border text-xs font-bold text-center tracking-wide animate-pulse ${notification.type === 'error' ? 'bg-red-900/20 border-red-500 text-red-400' : 'bg-green-900/20 border-green-500 text-green-400'}`}>{notification.message}</div>}
                <form onSubmit={isRegistering ? handleRegister : handleLogin}>
                  <Input label="Username" type="text" placeholder="ENTER USERNAME" value={formData.username} onChange={(e) => setFormData({...formData, username: e.target.value})} required icon={User} />
                  <Input label="Password" type="password" placeholder={isRegistering ? "MIN 6 CHARACTERS" : "••••••••"} value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} required icon={Lock} />
                  
                  {isRegistering && (
                      <Select 
                        label="Game Category" 
                        value={formData.category} 
                        onChange={(e) => setFormData({...formData, category: e.target.value})} 
                        // PERUBAHAN: Urutan opsi diubah, Togel pertama
                        options={[
                            { value: 'togel', label: 'TOGEL' },
                            { value: 'slot', label: 'SLOT' }
                        ]}
                        icon={Gamepad2}
                      />
                  )}

                  <Button type="submit" variant="primary" className="w-full mb-6" disabled={authLoading}>
                      {authLoading ? <Loader className="animate-spin" size={20} /> : (isRegistering ? 'CREATE ACCOUNT' : 'ACCESS SYSTEM')} 
                      {!authLoading && <ChevronRight size={16} />}
                  </Button>
                </form>
                <div className="text-center text-xs text-gray-500 uppercase tracking-wider">
                  {isRegistering ? <p>Has Access? <button onClick={() => setIsRegistering(false)} className="text-yellow-500 font-bold hover:underline">Login Protocol</button></p> : <p>No Access? <button onClick={() => setIsRegistering(true)} className="text-yellow-500 font-bold hover:underline">Register Protocol</button></p>}
                </div>
            </Card>
        </div>
      </div>
    );
  }

  const currentLevelInfo = getLevelInfo(currentUser.points);

  // Leaderboard Logic (Top 10) - Defined here for user view usage as well
  const topUsers = [...users]
    .filter(u => u.role !== 'admin')
    .sort((a, b) => b.points - a.points)
    .slice(0, 10);

  // Filtered Rewards Logic (For User)
  const availableRewards = rewards.filter(r => {
      // Show rewards that match user category
      return r.category === currentUser?.category;
  });

  return (
    <div className="min-h-screen font-sans text-gray-300 selection:bg-yellow-500/30 selection:text-yellow-200">
      <Background />
      <nav className="bg-black/50 backdrop-blur-md border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3 group cursor-pointer">
            <div className="group-hover:rotate-12 transition duration-300">
                <img src="https://img-zone.b-cdn.net/logo.png" alt="Logo" className="w-10 h-10 object-contain" />
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-bold text-white tracking-wider">{currentUser.username}</p>
              <div className="flex items-center justify-end gap-2">
                 <span className="text-[10px] text-gray-500 uppercase tracking-widest">{currentUser.role}</span>
                 {currentUser.role !== 'admin' && (
                    <>
                        <span className={`text-[10px] uppercase font-bold text-${currentLevelInfo.color === 'platinum' ? 'cyan' : 'yellow'}-500`}>{currentLevelInfo.name}</span>
                        <Badge color={currentUser.category === 'slot' ? 'blue' : 'purple'}>{currentUser.category}</Badge>
                    </>
                 )}
              </div>
            </div>
            <button onClick={handleLogout} className="bg-red-900/20 hover:bg-red-600 text-red-500 hover:text-white p-3 rounded-xl border border-red-500/30 transition-all duration-300"><LogOut size={18} /></button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-10">
        {notification && (
          <div className={`fixed top-24 right-6 z-50 px-6 py-4 rounded-xl shadow-2xl backdrop-blur-md border border-l-4 flex items-center gap-3 animate-slide-in-right ${notification.type === 'error' ? 'bg-black/80 border-red-500 border-l-red-500 text-red-400' : 'bg-black/80 border-green-500 border-l-green-500 text-green-400'}`}>
            {notification.type === 'error' ? <XCircle size={20} /> : <CheckCircle size={20} />}
            <span className="font-bold uppercase tracking-wide text-sm">{notification.message}</span>
          </div>
        )}

        {currentUser.role === 'admin' ? (
          // --- ADMIN VIEW ---
          <div className="space-y-8 animate-fade-in-up">
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { label: 'Total Members', val: users.filter(u => u.role !== 'admin').length, icon: Users, color: 'text-purple-400' },
                { label: 'Active Rewards', val: rewards.length, icon: Gift, color: 'text-pink-400' },
                { label: 'Pending Claims', val: claims.length, icon: ListChecks, color: 'text-orange-400' }
              ].map((stat, idx) => (
                <div key={idx} className="bg-neutral-900/50 backdrop-blur border border-white/5 p-6 rounded-2xl flex items-center justify-between hover:bg-neutral-900 transition duration-300 group">
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-widest mb-1">{stat.label}</p>
                    <p className="text-4xl font-bold text-white group-hover:scale-105 transition-transform origin-left">{stat.val}</p>
                  </div>
                  <div className={`p-4 rounded-2xl bg-white/5 border border-white/5 ${stat.color}`}><stat.icon size={28} /></div>
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {/* Generator Voucher */}
              <div className="lg:col-span-1 space-y-8">
                <Card title="Voucher Protocol">
                    <div className="flex flex-col gap-4">
                    <div>
                        <Input label="Points Value" type="number" value={newVoucherPoints} onChange={(e) => setNewVoucherPoints(e.target.value)} />
                        <Button onClick={handleCreateVoucher} variant="primary" className="w-full"><Plus size={18} /> GENERATE CODE</Button>
                    </div>
                    </div>
                </Card>
                
                <Card title="Reward Protocol">
                    <div className="flex flex-col gap-3">
                        <Input label="Reward Name" placeholder="e.g. iPhone 15" value={newReward.name} onChange={(e) => setNewReward({...newReward, name: e.target.value})} icon={Gift} />
                        <Input label="Image URL" placeholder="https://..." value={newReward.image} onChange={(e) => setNewReward({...newReward, image: e.target.value})} icon={ImageIcon} />
                        
                        <Select 
                            label="Target User" 
                            value={newReward.category} 
                            onChange={(e) => setNewReward({...newReward, category: e.target.value})} 
                            options={[
                                { value: 'slot', label: 'SLOT PLAYERS' },
                                { value: 'togel', label: 'TOGEL PLAYERS' }
                            ]}
                            icon={Target}
                        />

                        <div className="flex gap-2">
                           <div className="w-1/2">
                               <Input label="Points Cost" type="number" value={newReward.points} onChange={(e) => setNewReward({...newReward, points: e.target.value})} icon={Target} />
                           </div>
                           <div className="w-1/2">
                               <Input label="Stock" type="number" value={newReward.quantity} onChange={(e) => setNewReward({...newReward, quantity: e.target.value})} icon={Hash} />
                           </div>
                        </div>
                        <TextArea 
                          label="Terms & Conditions"
                          placeholder="e.g. Must be Gold member..." 
                          value={newReward.terms} 
                          onChange={(e) => setNewReward({...newReward, terms: e.target.value})} 
                        />
                        <Button onClick={handleCreateReward} variant="success" className="w-full"><Plus size={18} /> ADD REWARD</Button>
                    </div>
                 </Card>
              </div>

              {/* Member Database (Enhanced) */}
              <div className="lg:col-span-2 space-y-8">
                <Card title="Member Database" className="flex flex-col h-[500px]" action={
                    <Button onClick={() => setIsResetConfirmOpen(true)} variant="danger" className="!py-1 !px-3 !text-[10px]"><RotateCcw size={14} /> RESET ALL</Button>
                }>
                    <div className="flex gap-4 mb-4">
                        <div className="relative flex-1">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                            <input 
                                type="text" 
                                placeholder="SEARCH USER..." 
                                className="w-full bg-black/50 border border-gray-700 rounded-lg py-2 pl-10 pr-4 text-xs text-white focus:border-yellow-500 outline-none"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="overflow-y-auto pr-2 custom-scrollbar flex-1">
                    <table className="w-full text-left border-collapse">
                        <thead className="sticky top-0 bg-neutral-900 z-10">
                        <tr className="text-gray-500 text-xs uppercase tracking-widest border-b border-gray-800">
                            <th className="py-3 pl-2 cursor-pointer hover:text-white" onClick={() => requestSort('username')}>User <ArrowUpDown size={10} className="inline" /></th>
                            <th className="py-3 cursor-pointer hover:text-white" onClick={() => requestSort('points')}>Points <ArrowUpDown size={10} className="inline" /></th>
                            <th className="py-3">Type</th>
                            <th className="py-3 text-right">Detail</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800">
                        {sortedUsers.filter(u => u.role !== 'admin').map(user => {
                            const lvl = getLevelInfo(user.points);
                            return (
                            <tr key={user.id} className="hover:bg-white/5 transition-colors group">
                                <td className="py-3 pl-2 font-bold text-white text-sm">{user.username}</td>
                                <td className="py-3 font-mono text-emerald-400 text-sm">{formatNumber(user.points)}</td>
                                <td className="py-3">
                                    <Badge color={user.category === 'slot' ? 'blue' : 'purple'}>{user.category}</Badge>
                                </td>
                                <td className="py-3 text-right">
                                    <button onClick={() => setSelectedUser(user)} className="p-2 bg-white/5 hover:bg-yellow-500 hover:text-black rounded-lg transition-colors"><Eye size={16} /></button>
                                </td>
                            </tr>
                            );
                        })}
                        </tbody>
                    </table>
                    </div>
                </Card>

                {/* Claim Registry */}
                <Card title="Claim Registry">
                    <div className="overflow-x-auto max-h-[300px] custom-scrollbar">
                        <table className="w-full text-left border-collapse text-sm">
                            <thead className="sticky top-0 bg-neutral-900 z-10">
                                <tr className="text-gray-500 text-xs uppercase tracking-widest border-b border-gray-800">
                                    <th className="py-3 pl-2">Time</th>
                                    <th className="py-3">User</th>
                                    <th className="py-3">Reward</th>
                                    <th className="py-3">Claim Code</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800">
                                {claims.map(c => (
                                    <tr key={c.id} className="hover:bg-white/5 transition-colors">
                                        <td className="py-3 pl-2 text-gray-500 text-xs font-mono">{formatDate(c.createdAt)}</td>
                                        <td className="py-3 font-bold text-white">{c.username}</td>
                                        <td className="py-3 text-yellow-500">{c.rewardName}</td>
                                        <td className="py-3 font-mono text-emerald-400 font-bold tracking-wider">{c.claimCode}</td>
                                    </tr>
                                ))}
                                {claims.length === 0 && <tr><td colSpan="4" className="py-4 text-center text-gray-600 text-xs">NO CLAIMS YET</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </Card>
              </div>
            </div>

            {/* Active Rewards List */}
            <Card title="Active Reward Targets">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {rewards.map(r => (
                        <div key={r.id} className="relative bg-black/40 border border-white/10 rounded-xl p-4 hover:border-pink-500/50 transition group">
                            <button onClick={() => handleDeleteReward(r.id)} className="absolute top-2 right-2 text-gray-600 hover:text-red-500 z-10"><Trash2 size={16}/></button>
                            <div className="h-32 w-full bg-black/50 rounded-lg mb-3 flex items-center justify-center overflow-hidden border border-white/5">
                                {r.image ? <img src={r.image} alt={r.name} className="w-full h-full object-cover" /> : <Gift size={32} className="text-gray-700" />}
                            </div>
                            <h4 className="text-white font-bold uppercase text-sm mb-1 truncate">{r.name}</h4>
                            <div className="flex justify-between items-center text-xs mb-1">
                                <span className={`${r.quantity === 0 ? 'text-red-500' : 'text-gray-500'}`}>Qty: {r.quantity !== undefined ? r.quantity : '∞'}</span>
                                <Badge color={r.category === 'slot' ? 'blue' : 'purple'} className="!text-[8px] !px-1.5 !py-0.5">{r.category}</Badge>
                            </div>
                            <div className="flex items-center gap-1 text-yellow-500 font-mono font-bold text-xs">
                                <Target size={12} /> {formatNumber(r.points)} PTS
                            </div>
                        </div>
                    ))}
                    {rewards.length === 0 && <p className="text-gray-500 text-xs col-span-4 text-center py-4">NO REWARDS CONFIGURED</p>}
                </div>
            </Card>

            {/* Voucher Database */}
            <Card title="Voucher Database">
                 <div className="flex flex-col md:flex-row gap-4 mb-4 justify-between">
                    <div className="relative flex-1 max-w-md">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                        <input 
                            type="text" 
                            placeholder="SEARCH CODE..." 
                            className="w-full bg-black/50 border border-gray-700 rounded-lg py-2 pl-10 pr-4 text-xs text-white focus:border-yellow-500 outline-none"
                            value={voucherSearchTerm}
                            onChange={(e) => setVoucherSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="flex gap-2">
                        {['all', 'active', 'used'].map(f => (
                            <button 
                                key={f}
                                onClick={() => setVoucherFilter(f)}
                                className={`px-3 py-1 rounded text-xs font-bold uppercase transition-colors ${voucherFilter === f ? 'bg-yellow-500 text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                 </div>

                 <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-sm">
                        <thead>
                            <tr className="text-gray-500 text-xs uppercase tracking-widest border-b border-gray-800">
                                <th className="py-3 pl-4">Created</th>
                                <th className="py-3">Code</th>
                                <th className="py-3">Value</th>
                                <th className="py-3">Status</th>
                                <th className="py-3">Used By</th>
                                <th className="py-3 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800">
                            {filteredVouchers.map(v => (
                                <tr key={v.id} className="hover:bg-white/5 transition-colors">
                                    <td className="py-3 pl-4 text-gray-500 text-xs font-mono">{formatDate(v.createdAt)}</td>
                                    <td className="py-3 font-mono font-bold text-white tracking-wider">{v.code}</td>
                                    <td className="py-3 font-bold text-yellow-500">{v.points}</td>
                                    <td className="py-3">
                                        {v.isUsed ? <Badge color="red">USED</Badge> : <Badge color="green">ACTIVE</Badge>}
                                    </td>
                                    <td className="py-3 text-gray-400 text-xs">{v.usedBy || '-'}</td>
                                    <td className="py-3 text-right">
                                         <button onClick={() => handleDeleteVoucher(v.id)} className="text-gray-600 hover:text-red-500 transition-colors"><Trash2 size={16} /></button>
                                    </td>
                                </tr>
                            ))}
                            {filteredVouchers.length === 0 && (
                                <tr><td colSpan="6" className="py-8 text-center text-gray-600 text-xs">NO DATA FOUND</td></tr>
                            )}
                        </tbody>
                    </table>
                 </div>
            </Card>

            {/* Modals */}
            <Modal isOpen={selectedUser !== null} onClose={() => setSelectedUser(null)} title="Member Intelligence">
                {selectedUser && (
                    <div className="space-y-6">
                        <div className="flex items-center gap-4 bg-white/5 p-4 rounded-xl border border-white/10">
                            <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-red-600 rounded-full flex items-center justify-center text-black font-bold text-2xl">
                                {selectedUser.username.charAt(0).toUpperCase()}
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-white">{selectedUser.username}</h2>
                                <p className="text-gray-400 text-sm">{selectedUser.email}</p>
                                <div className="mt-2 flex gap-2">
                                    <Badge color={getLevelInfo(selectedUser.points).color}>{getLevelInfo(selectedUser.points).name}</Badge>
                                    <Badge color="green">{formatNumber(selectedUser.points)} PTS</Badge>
                                    <Badge color={selectedUser.category === 'slot' ? 'blue' : 'purple'}>{selectedUser.category}</Badge>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h4 className="text-gray-500 uppercase text-xs font-bold mb-3 tracking-widest">History Log</h4>
                            <div className="space-y-2">
                                {selectedUser.history && selectedUser.history.length > 0 ? (
                                    selectedUser.history.map((h, i) => (
                                        <div key={i} className="flex justify-between items-center p-3 bg-black/30 rounded border border-white/5 text-sm">
                                            <div>
                                                <div className="text-white font-bold">{h.activity}</div>
                                                <div className="text-gray-600 text-[10px]">{h.date}</div>
                                            </div>
                                            <div className={`font-mono font-bold ${h.amount === 'RESET' ? 'text-red-500' : 'text-emerald-500'}`}>{h.amount}</div>
                                        </div>
                                    ))
                                ) : <p className="text-gray-600 text-sm">No history data.</p>}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            <Modal isOpen={isResetConfirmOpen} onClose={() => setIsResetConfirmOpen(false)} title="CRITICAL ACTION">
                <div className="text-center p-4">
                    <AlertTriangle size={48} className="text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-white mb-2">RESET ALL USER POINTS?</h2>
                    <p className="text-gray-400 text-sm mb-6">This action cannot be undone. All user points will be set to 0 and a reset log will be created.</p>
                    <div className="flex gap-4 justify-center">
                        <Button onClick={() => setIsResetConfirmOpen(false)} variant="secondary">CANCEL</Button>
                        <Button onClick={handleResetAllPoints} variant="danger">YES, WIPE DATA</Button>
                    </div>
                </div>
            </Modal>

          </div>
        ) : (
          // --- USER VIEW ---
          <div className="space-y-8 animate-fade-in-up">
            
            <div className={`relative w-full overflow-hidden rounded-3xl p-8 border ${currentLevelInfo.border} shadow-2xl transition-all duration-500 hover:scale-[1.01]`}>
              <div className={`absolute inset-0 bg-gradient-to-br ${currentLevelInfo.gradient} opacity-90`}></div>
              <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-200%] animate-[shimmer_3s_infinite]"></div>

              <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-8">
                <div className="text-center md:text-left">
                  <div className="flex items-center gap-3 mb-2 justify-center md:justify-start">
                     <div className="p-2 bg-black/30 rounded-full border border-white/10"><currentLevelInfo.icon size={20} className={currentLevelInfo.textColor} /></div>
                     <span className={`text-sm font-bold uppercase tracking-[0.3em] ${currentLevelInfo.textColor}`}>{currentLevelInfo.name} Tier</span>
                  </div>
                  <div className="text-6xl md:text-7xl font-bold text-white drop-shadow-lg tracking-tighter">{formatNumber(currentUser.points)}<span className="text-2xl text-white/50 ml-2 font-light">PTS</span></div>
                  <p className="mt-4 text-white/60 font-mono text-xs tracking-widest uppercase">ID: {currentUser.username} • Secure Hash Verified</p>
                </div>
                <div className="relative w-32 h-32 flex items-center justify-center">
                    <div className="absolute inset-0 rounded-full border-4 border-white/10"></div>
                    <div className={`absolute inset-0 rounded-full border-4 border-t-transparent ${currentLevelInfo.textColor.replace('text', 'border')} animate-[spin_3s_linear_infinite]`}></div>
                    <img src="https://img-zone.b-cdn.net/logo.png" alt="Logo" className="w-16 h-16 object-contain drop-shadow-[0_0_15px_rgba(255,255,255,0.5)]" />
                </div>
              </div>
            </div>

             {/* Leaderboard Card (NEW) */}
             <Card title="Top 10 Leaderboard">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="text-gray-500 text-xs uppercase tracking-widest border-b border-gray-800">
                                <th className="py-3 pl-2">#</th>
                                <th className="py-3">Member</th>
                                <th className="py-3">Type</th>
                                <th className="py-3 text-right">Points</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800">
                            {topUsers.map((user, idx) => (
                                <tr key={user.id} className="hover:bg-white/5 transition-colors">
                                    <td className="py-3 pl-2 text-yellow-500 font-bold">
                                        {idx + 1 === 1 ? <Crown size={16} /> : idx + 1}
                                    </td>
                                    <td className="py-3 font-bold text-white flex items-center gap-2">
                                        {user.username}
                                        {idx + 1 <= 3 && <Trophy size={14} className="text-yellow-500" />}
                                    </td>
                                    <td className="py-3">
                                        {/* Display Icon based on category */}
                                        {user.category === 'slot' ? (
                                            <Gamepad2 size={16} className="text-blue-400" />
                                        ) : (
                                            <Dices size={16} className="text-purple-400" />
                                        )}
                                    </td>
                                    <td className="py-3 text-right font-mono text-emerald-400 font-bold">
                                        {formatNumber(user.points)}
                                    </td>
                                </tr>
                            ))}
                            {topUsers.length === 0 && <tr><td colSpan="4" className="py-4 text-center text-gray-500 text-xs">NO DATA</td></tr>}
                        </tbody>
                    </table>
                </div>
             </Card>

             {/* Rewards Showcase for User (FILTERED) */}
             {availableRewards.length > 0 ? (
                 <div className="bg-neutral-900/50 backdrop-blur border border-white/10 rounded-xl p-6">
                    <h3 className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-4">Redemption Targets ({currentUser.category})</h3>
                    <div className="flex gap-4 overflow-x-auto pb-4 custom-scrollbar">
                        {availableRewards.map(r => {
                            const progress = Math.min((currentUser.points / r.points) * 100, 100);
                            const isUnlock = currentUser.points >= r.points;
                            const isStockAvailable = r.quantity === undefined || r.quantity > 0;
                            
                            return (
                                <div key={r.id} className={`flex-shrink-0 w-48 bg-black/40 border ${isUnlock && isStockAvailable ? 'border-yellow-500 shadow-[0_0_15px_rgba(234,179,8,0.2)]' : 'border-white/10'} rounded-xl p-3 relative group transition-all`}>
                                    <div className="h-24 bg-black/50 rounded-lg mb-2 overflow-hidden flex items-center justify-center relative">
                                        {r.image ? <img src={r.image} alt={r.name} className={`w-full h-full object-cover transition duration-500 ${isUnlock && isStockAvailable ? '' : 'grayscale opacity-50'}`} /> : <Gift className={isUnlock && isStockAvailable ? 'text-yellow-500' : 'text-gray-700'} />}
                                        
                                        {/* CLAIM OVERLAY */}
                                        {isUnlock && isStockAvailable ? (
                                            <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition duration-300 gap-2">
                                                <button 
                                                    onClick={() => handleClaimReward(r)}
                                                    className="bg-yellow-500 text-black font-bold text-xs px-3 py-1 rounded-full uppercase hover:bg-yellow-400 hover:scale-105 transition-transform"
                                                >
                                                    CLAIM
                                                </button>
                                                {r.terms && (
                                                   <button 
                                                      onClick={() => setSelectedRewardInfo(r)}
                                                      className="text-white text-[10px] underline hover:text-yellow-200"
                                                   >
                                                      T&C
                                                   </button>
                                                )}
                                            </div>
                                        ) : !isStockAvailable ? (
                                            <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                                                <span className="text-red-500 font-bold text-xs border border-red-500 px-2 py-1 rounded">OUT OF STOCK</span>
                                            </div>
                                        ) : null}
                                    </div>
                                    <div className="flex justify-between items-start">
                                        <h4 className={`font-bold text-sm truncate ${isUnlock ? 'text-white' : 'text-gray-500'}`}>{r.name}</h4>
                                        {r.terms && <Info size={12} className="text-gray-600 hover:text-white cursor-pointer" onClick={() => setSelectedRewardInfo(r)} />}
                                    </div>
                                    
                                    <div className="flex justify-between items-center mb-1">
                                        <p className="text-xs font-mono text-yellow-600">{formatNumber(r.points)} PTS</p>
                                        <p className={`text-[10px] ${r.quantity === 0 ? 'text-red-500' : 'text-gray-500'}`}>Stock: {r.quantity !== undefined ? r.quantity : '∞'}</p>
                                    </div>
                                    
                                    <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
                                        <div className={`h-full ${isUnlock ? 'bg-yellow-500' : 'bg-gray-600'}`} style={{ width: `${progress}%` }}></div>
                                    </div>
                                    
                                    {isUnlock && isStockAvailable ? (
                                        <div className="absolute top-2 right-2 bg-yellow-500 text-black text-[8px] font-bold px-1 rounded animate-pulse">UNLOCK</div>
                                    ) : !isUnlock ? (
                                        <div className="absolute top-2 right-2 bg-black/60 text-gray-400 text-[8px] font-bold px-1 rounded backdrop-blur">LOCKED</div>
                                    ) : null}
                                </div>
                            )
                        })}
                    </div>
                 </div>
             ) : (
                 <div className="bg-neutral-900/50 backdrop-blur border border-white/10 rounded-xl p-6 text-center">
                    <p className="text-gray-500 text-xs">NO REWARDS AVAILABLE FOR YOUR CATEGORY ({currentUser.category.toUpperCase()})</p>
                 </div>
             )}

            <div className="grid lg:grid-cols-2 gap-8">
              <Card title="Voucher Redemption" className="border-t-4 border-t-yellow-500">
                <form onSubmit={handleRedeemVoucher}>
                  <p className="text-gray-400 mb-6 text-sm">Enter the 8-digit secure code found on your physical voucher or digital receipt.</p>
                  <div className="relative mb-6 group">
                    <input type="text" className="w-full bg-black border-2 border-gray-800 text-center text-3xl font-mono font-bold tracking-[0.5em] py-6 rounded-xl text-yellow-500 uppercase focus:outline-none focus:border-yellow-500 focus:shadow-[0_0_30px_rgba(234,179,8,0.2)] transition-all duration-300 placeholder-gray-800" placeholder="--------" maxLength={8} value={voucherCodeInput} onChange={(e) => setVoucherCodeInput(e.target.value)} />
                  </div>
                  <Button type="submit" variant="primary" className="w-full text-lg py-4" disabled={!voucherCodeInput || authLoading}>{authLoading ? 'VERIFYING...' : 'CLAIM REWARD'}</Button>
                </form>
              </Card>

              <Card title="Transaction Log">
                <div className="overflow-y-auto max-h-[300px] pr-2 custom-scrollbar">
                  {currentUser.history && currentUser.history.length > 0 ? (
                    <div className="space-y-4">
                      {currentUser.history.map((item) => (
                        <div 
                          key={item.id} 
                          onClick={() => {
                             if (item.code) {
                                setClaimSuccessData({
                                   code: item.code,
                                   rewardName: item.rewardName || item.activity.replace('CLAIM: ', ''),
                                   rewardImage: item.rewardImage
                                });
                                setIsClaimModalOpen(true);
                             }
                          }}
                          className={`relative flex items-center justify-between p-4 bg-black/40 rounded-xl border border-white/5 transition-all group overflow-hidden ${item.code ? 'cursor-pointer hover:border-yellow-500/50 hover:bg-white/5' : ''}`}
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/5 to-transparent opacity-0 group-hover:opacity-100 transition duration-500"></div>
                          <div className="relative flex items-center gap-4">
                            <div className="p-3 bg-gradient-to-br from-gray-800 to-black rounded-lg border border-gray-700 text-green-500 shadow-lg">
                                {item.activity.includes('CLAIM') ? <Gift size={18} className="text-yellow-500" /> : <Plus size={18} />}
                            </div>
                            <div>
                              <p className="font-bold text-gray-200 text-sm tracking-wide uppercase flex items-center gap-2">
                                {item.activity}
                                {item.code && <Eye size={12} className="text-yellow-500 animate-pulse" />}
                              </p>
                              <p className="text-[10px] text-gray-500 font-mono mt-1">{item.date}</p>
                            </div>
                          </div>
                          <span className={`relative font-mono font-bold text-lg shadow-black drop-shadow-md ${item.amount.includes('-') ? 'text-red-400' : 'text-emerald-400'}`}>{item.amount}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-600 py-10"><History size={48} className="mb-4 opacity-20" /><p className="text-xs uppercase tracking-widest">No Cloud Data</p></div>
                  )}
                </div>
              </Card>
            </div>

            {/* CLAIM SUCCESS MODAL */}
            <Modal isOpen={isClaimModalOpen} onClose={() => setIsClaimModalOpen(false)} title="REWARD CLAIMED">
                {claimSuccessData && (
                    <div className="text-center p-4">
                        <div className="w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-[0_0_30px_rgba(234,179,8,0.5)]">
                             <Award size={48} className="text-black" />
                        </div>
                        
                        <h2 className="text-2xl font-bold text-white uppercase tracking-widest mb-1">CONGRATULATIONS!</h2>
                        <p className="text-gray-400 text-sm mb-6">You have successfully claimed:</p>
                        
                        <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
                            <h3 className="text-xl font-bold text-yellow-500 mb-2">{claimSuccessData.rewardName}</h3>
                            <div className="flex flex-col items-center gap-2">
                                <span className="text-xs text-gray-500 uppercase tracking-widest">YOUR CLAIM CODE</span>
                                <div className="bg-black/50 border border-yellow-500/50 rounded px-4 py-2 text-2xl font-mono font-bold text-white tracking-widest">
                                    {claimSuccessData.code}
                                </div>
                                <p className="text-[10px] text-gray-500 mt-1">Please save this code.</p>
                            </div>
                        </div>

                        <p className="text-gray-400 text-sm mb-4">
                            To finalize your reward collection, please contact our Admin Team immediately with your Claim Code.
                        </p>

                        <div className="flex justify-center">
                            <Button onClick={openLiveChat} variant="whatsapp" className="w-full">
                                <MessageCircle size={18} /> CONTACT ADMIN (LIVE CHAT)
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>

            {/* REWARD TERMS MODAL */}
            <Modal isOpen={selectedRewardInfo !== null} onClose={() => setSelectedRewardInfo(null)} title="Terms & Conditions">
                {selectedRewardInfo && (
                    <div className="p-4">
                         <div className="flex items-center gap-4 mb-6">
                            <div className="w-16 h-16 bg-black/50 rounded-lg overflow-hidden border border-white/10 shrink-0">
                                {selectedRewardInfo.image ? <img src={selectedRewardInfo.image} alt="" className="w-full h-full object-cover" /> : <Gift className="text-gray-700 m-auto mt-4" />}
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-white">{selectedRewardInfo.name}</h3>
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-yellow-500 font-mono font-bold">{formatNumber(selectedRewardInfo.points)} PTS</span>
                                    <span className="text-gray-500">•</span>
                                    <span className={`${selectedRewardInfo.quantity === 0 ? 'text-red-500' : 'text-gray-400'}`}>Stock: {selectedRewardInfo.quantity ?? '∞'}</span>
                                </div>
                            </div>
                         </div>
                         
                         <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                            <h4 className="text-xs text-gray-400 uppercase font-bold mb-3 tracking-widest flex items-center gap-2">
                                <FileText size={14} /> Rules & Requirements
                            </h4>
                            <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-line">
                                {selectedRewardInfo.terms || "No specific terms and conditions for this reward."}
                            </p>
                         </div>
                    </div>
                )}
            </Modal>
          </div>
        )}
      </main>
      <style>{`.custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; } .custom-scrollbar::-webkit-scrollbar-track { background: rgba(0, 0, 0, 0.2); } .custom-scrollbar::-webkit-scrollbar-thumb { background: #333; border-radius: 10px; } .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #555; } @keyframes shimmer { 0% { transform: translateX(-200%); } 100% { transform: translateX(200%); } }`}</style>
    </div>
  );
}